import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faMap, faMapPin, faSitemap } from "@fortawesome/pro-regular-svg-icons"
import { faGear, faPhotoFilmMusic } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Tab, Tabs, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { IForm, SUMMARY_TABS } from "form/model"
import { activateSummaryTab, getAvailableSummaryTabs } from "form/tools/summary-tabs"
import React from "react"
import { connect } from "react-redux"
import { ReportCoordinates } from "reports/components/summary/ReportCoordinates"
import { ReportCreatives } from "reports/components/summary/ReportCreatives"
import { ReportOperators } from "reports/components/summary/ReportOperators"
import { ReportShapes } from "reports/components/summary/ReportShapes"
import { FlexBox } from "ui/components/FlexBox"
import { addImportant } from "ui/tools/common"
import { SummaryParameters } from "./parameters/SummaryParameters"

interface ISummaryCampaignProps {
  form: IForm
  contentWidth: number
}

interface ISummaryTabLabelProps {
  icon: IconProp
  tab: SUMMARY_TABS
}

const TabLabel = React.memo(({ icon, tab }: ISummaryTabLabelProps) => (
  <FlexBox id={`summary-tab-${tab}`} gap="1rem" sx={{ position: "relative" }}>
    <FontAwesomeIcon icon={icon} size="lg" />
    <Typography
      id={`summary-tab-${tab}-label`}
      sx={{ color: theme => addImportant(theme.palette.text.primary2 as string), textTransform: "initial" }}>
      {translate(`campaign.summary.tab.${tab}`)}
    </Typography>
  </FlexBox>
))

const SummaryCampaignFn = React.memo(({ form, contentWidth }: ISummaryCampaignProps): JSX.Element => {
  const availableTabs = getAvailableSummaryTabs(form)
  let Content
  switch (form.activeSummaryTab) {
    case SUMMARY_TABS.PARAMETERS:
      Content = SummaryParameters
      break
    case SUMMARY_TABS.REPORT_OPERATORS:
      Content = ReportOperators
      break
    case SUMMARY_TABS.REPORT_CREATIVES:
      Content = ReportCreatives
      break
    case SUMMARY_TABS.REPORT_SHAPES:
      Content = ReportShapes
      break
    case SUMMARY_TABS.REPORT_COORDINATES:
      Content = ReportCoordinates
      break
    default:
      throw Error(`Summary tab "${form.activeSummaryTab}" not implemented yet`)
  }
  return (
    <FlexBox vertical fullWidth sx={{ overflow: "auto" }}>
      <Tabs
        value={form.activeSummaryTab}
        onChange={(_event, newTab: SUMMARY_TABS) => activateSummaryTab(form, newTab)}
        sx={{
          "mt": "1rem",
          "maxWidth": "100%",
          "borderBottom": theme => theme.palette.border,
          "& .MuiButtonBase-root.MuiTab-root.Mui-selected p": {
            color: theme => theme.palette.text.primary2,
            fontWeight: 500
          },
          "& .MuiButtonBase-root.MuiTab-root": {
            fontWeight: 300,
            minWidth: "11rem"
          },
          "@media (max-width: 1300px)": {
            "& .MuiButtonBase-root.MuiTab-root": {
              minWidth: "auto"
            }
          },
          "& .MuiTabs-scrollButtons svg": {
            color: theme => theme.palette.text.primary2
          }
        }}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile>
        <Tab
          value={SUMMARY_TABS.PARAMETERS}
          disabled={!availableTabs.includes(SUMMARY_TABS.PARAMETERS)}
          label={<TabLabel tab={SUMMARY_TABS.PARAMETERS} icon={faGear} />}
        />
        <Tab
          value={SUMMARY_TABS.REPORT_OPERATORS}
          disabled={!availableTabs.includes(SUMMARY_TABS.REPORT_OPERATORS)}
          label={<TabLabel tab={SUMMARY_TABS.REPORT_OPERATORS} icon={faSitemap} />}
        />
        <Tab
          value={SUMMARY_TABS.REPORT_CREATIVES}
          disabled={!availableTabs.includes(SUMMARY_TABS.REPORT_CREATIVES)}
          label={<TabLabel tab={SUMMARY_TABS.REPORT_CREATIVES} icon={faPhotoFilmMusic} />}
        />
        <Tab
          value={SUMMARY_TABS.REPORT_SHAPES}
          disabled={!availableTabs.includes(SUMMARY_TABS.REPORT_SHAPES)}
          label={<TabLabel tab={SUMMARY_TABS.REPORT_SHAPES} icon={faMap} />}
        />
        <Tab
          value={SUMMARY_TABS.REPORT_COORDINATES}
          disabled={!availableTabs.includes(SUMMARY_TABS.REPORT_COORDINATES)}
          label={<TabLabel tab={SUMMARY_TABS.REPORT_COORDINATES} icon={faMapPin} />}
        />
      </Tabs>
      <Content form={form} contentWidth={contentWidth} isForecast={false} isDspDeal={false} />
    </FlexBox>
  )
})

export const SummaryCampaign = connect(
  (state: IRootState): ISummaryCampaignProps => ({
    form: state.form.campaignEditForm.form,
    contentWidth: state.ui.contentWidth
  })
)(SummaryCampaignFn)
